import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiFile } from 'react-icons/bi';
import { FaExchangeAlt } from 'react-icons/fa';

import { useAppDispatch, useAppSelector } from '@/app/hook';
import HintBlock from '@/components/common/HintBlock';
import { PSButton } from '@/components/ui/button';
import { MultiLanguageUrl } from '@/constants/multiLanguage.constant';
import { selectUseId } from '@/features/auth/auth.slice';
import { setCurrentScenarioTitle } from '@/features/scenario.slice';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import Utilities from '@/utils';

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const color =
    status === 'PAID'
      ? 'tw-bg-green-100 tw-text-green-700'
      : status === 'PENDING'
      ? 'tw-bg-yellow-100 tw-text-yellow-700'
      : status === 'WAITING_FOR_PAYMENT'
      ? 'tw-bg-indigo-100 tw-text-indigo-700'
      : 'tw-bg-gray-100 tw-text-gray-700';
  return <span className={`tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-bold ${color}`}>{Utilities.renderTransactionStatusTitle(status, t)}</span>;
}

function TransactionCard({ tx }: { tx: any }) {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const userId = useAppSelector(selectUseId);

  const lastMessage = tx?.message?.[tx?.message?.length - 1];

  // Helper to render a preview for the last message (file, image, video, audio, or text)
  const renderLastMessagePreview = () => {
    if (!lastMessage || !lastMessage.message || lastMessage.sender === userId) return null;
    const value = lastMessage.message;
    if (typeof value !== 'string') return null;
    const lower = value.toLowerCase();
    // Image
    if (lower.match(/\.(png|jpe?g|gif|webp)$/)) {
      return (
        <a href={value} target="_blank" rel="noopener noreferrer" className="tw-block tw-mt-1">
          <img src={value} alt="uploaded" className="tw-max-w-[80px] tw-max-h-[60px] tw-rounded tw-border tw-border-gray-200" />
        </a>
      );
    }
    // Video
    if (lower.match(/\.(mp4|webm|ogg|mov|m4v|mkv|avi|3gp|mpeg)$/)) {
      return (
        <a href={value} target="_blank" rel="noopener noreferrer" className="tw-block tw-mt-1">
          <video src={value} className="tw-max-w-[80px] tw-max-h-[60px] tw-rounded tw-border tw-border-gray-200">
            <track kind="captions" />
          </video>
        </a>
      );
    }
    // Audio
    if (lower.match(/\.(mp3|aac|wav|ogg)$/)) {
      return (
        <a href={value} target="_blank" rel="noopener noreferrer" className="tw-block tw-mt-1">
          <audio src={value} controls className="tw-w-[80px]">
            <track kind="captions" />
          </audio>
        </a>
      );
    }
    // Generic file
    if (lower.match(/\.[a-z0-9]{2,5}$/) && !lower.match(/\.(png|jpe?g|gif|webp|mp4|webm|ogg|mov|m4v|mkv|avi|3gp|mpeg|mp3|aac|wav)$/)) {
      return (
        <a href={value} target="_blank" rel="noopener noreferrer" className="tw-flex tw-items-center tw-gap-2 tw-mt-1 tw-text-blue-600 tw-underline">
          <BiFile size={18} />
          <span className="tw-text-xs">{value.split('/').pop() || 'File'}</span>
        </a>
      );
    }
    // Text fallback
    return <span className="tw-text-xs tw-text-orange-600 tw-mt-1 tw-italic">{value}</span>;
  };

  return (
    <div className="tw-flex-wrap tw-bg-white tw-rounded-xl tw-shadow-md tw-border tw-border-gray-200 tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-mb-4 tw-font-['Lexend']">
      <div className="tw-flex tw-flex-col tw-gap-3 tw-flex-1">
        <div className="tw-flex-wrap tw-flex tw-items-center tw-gap-6 tw-flex-1">
          <FaExchangeAlt className="tw-w-8 tw-h-8 tw-text-blue-600" />
          <div className="tw-flex-wrap tw-flex tw-flex-col tw-gap-1">
            <span className="tw-font-bold tw-text-lg">{t('marketplace/transactions/transaction')}</span>
            <span className="tw-text-sm tw-text-gray-500">
              {t('marketplace/transactions/id')}: <span className="tw-font-mono">{tx.transaction_id}</span>
            </span>
            {/* Show last message preview if exists */}
            {tx.buyer_transaction_status === 'PENDING' && renderLastMessagePreview()}
          </div>
        </div>
        <div className="tw-flex-wrap tw-flex tw-flex-col tw-gap-1">
          <span className="tw-font-semibold">
            {t('marketplace/transactions/buyer')}: <span className="tw-font-mono">{tx.buyer}</span>
          </span>
          <span className="tw-text-xs tw-text-gray-400">
            {t('marketplace/transactions/created')}: {new Date(tx.creation_date_time).toLocaleString()}
          </span>
        </div>
        <div className="tw-flex tw-flex-wrap tw-flex-row tw-gap-2 tw-mt-4">
          <span>
            {t('marketplace/transactions/sellerStatus')}: <StatusBadge status={tx.payment_status} />
          </span>
          <span>
            {t('marketplace/transactions/buyerStatus')}: <StatusBadge status={tx.buyer_transaction_status} />
          </span>
        </div>
        <HintBlock texts={[t('marketplace/transactions/noteTransactionFinished')]} />
      </div>

      <div className="tw-w-full sm:tw-w-auto tw-mt-3 sm:tw-mt-0 tw-flex-wrap tw-flex tw-flex-col tw-gap-2 tw-items-end">
        <PSButton id="btn-transaction-show-detail" variant="outline" onClick={() => setShowModal(true)} title={t('marketplace/transactions/showDetail')} />
        <a
          href={`/${MultiLanguageUrl[i18n.language]}/control-panel/chatbots?transaction_id=${tx?.transaction_id}`}
          onClick={() => {
            dispatch(setCurrentScenarioTitle(tx?.transaction_id));
          }}
          className="tw-w-full sm:tw-w-auto tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-lg tw-shadow hover:tw-bg-blue-700 tw-transition tw-text-sm tw-font-normal"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('marketplace/transactions/goToChatbots')}
        </a>
      </div>
      {showModal && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-40 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-xl tw-shadow-lg tw-p-8 tw-min-w-[350px] tw-relative">
            <button
              id="btn-transaction-modal"
              className="tw-absolute tw-top-2 tw-right-2 tw-text-gray-400 tw-text-xl tw-border-none tw-bg-white"
              onClick={() => setShowModal(false)}
            >
              &times;
            </button>
            <h2 className="tw-font-bold tw-text-xl tw-mb-4  tw-font-['Lexend']">{t('marketplace/transactions/detailTitle')}</h2>
            <div className="tw-space-y-2 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
              <div>
                <strong>{t('marketplace/transactions/id')}:</strong> {tx.transaction_id}
              </div>
              <div>
                <strong>{t('marketplace/transactions/buyer')}:</strong> {tx.buyer}
              </div>
              <div>
                <strong>{t('marketplace/transactions/created')}:</strong> {new Date(tx.creation_date_time).toLocaleString()}
              </div>
              <div>
                <strong>{t('marketplace/transactions/paymentStatus')}:</strong> {Utilities.renderTransactionStatusTitle(tx.payment_status, t)}
              </div>
              <div>
                <strong>{t('marketplace/transactions/sellerStatus')}:</strong> {Utilities.renderTransactionStatusTitle(tx.seller_transaction_status, t)}
              </div>
              <div>
                <strong>{t('marketplace/transactions/buyerStatus')}:</strong> {Utilities.renderTransactionStatusTitle(tx.buyer_transaction_status, t)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WrappedErrorBoundary(TransactionCard);
